import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useListingsData } from '../hooks/useListingsData';
import { useClientData } from '../hooks/useClientData';
import type {
  ListingSummary,
  ListingDetail,
  ListingFilters,
  ListingTypeOption,
  ListingClientType,
  ListingDocumentType,
  ListingClientDocumentType,
  ListingClient,
  ListingImageType,
  CreateListingPayload,
  UpdateListingPayload,
  CreateClientPayload,
  UpdateClientPayload,
} from '../types/ListingTypes';

// ─── Context shape ────────────────────────────────────────────────────────────

export interface ListingContextType {
  // State
  listings: ListingSummary[];
  activeListing: ListingDetail | null;
  loading: boolean;
  error: string | null;

  // Load lists into state
  loadUserListings: (userId: string) => Promise<void>;
  loadAgencyListings: (agencyId: string) => Promise<void>;

  // Pure fetch (returns data, does not mutate shared listings state)
  fetchUserListings: (userId: string) => Promise<ListingSummary[]>;
  fetchAgencyListings: (agencyId: string) => Promise<ListingSummary[]>;

  // Active (detail) listing
  openListing: (id: string) => Promise<void>;
  clearActiveListing: () => void;

  // Listing CRUD – also keep local state in sync
  createListing: (payload: CreateListingPayload) => Promise<ListingSummary>;
  updateListing: (id: string, payload: UpdateListingPayload) => Promise<void>;
  deleteListing: (id: string) => Promise<void>;

  // Reference data
  fetchListingTypes: () => Promise<ListingTypeOption[]>;
  fetchDocumentTypes: (listingTypeId?: string) => Promise<ListingDocumentType[]>;
  fetchClientDocumentTypes: (clientTypeId?: string) => Promise<ListingClientDocumentType[]>;

  // Clients
  fetchClientTypes: (params?: { listingTypeId?: string; listingTypeName?: string }) => Promise<ListingClientType[]>;
  fetchClientsByListing: (listingId: string) => Promise<ListingClient[]>;
  createClient: (payload: CreateClientPayload) => Promise<ListingClient>;
  updateClient: (id: string, payload: UpdateClientPayload) => Promise<ListingClient>;
  deleteClient: (id: string) => Promise<void>;
  uploadClientDocument: (clientId: string, documentTypeId: string, name: string, file: File) => Promise<void>;
  uploadListingDocument: (listingId: string, documentTypeId: string, name: string, file: File) => Promise<void>;
  uploadListingImage: (listingId: string, imageType: ListingImageType, file: File) => Promise<void>;
  deleteListingImage: (imageId: string) => Promise<void>;
  deleteListingDocument: (documentId: string) => Promise<void>;
  deleteClientDocument: (documentId: string) => Promise<void>;
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ListingContext = createContext<ListingContextType | undefined>(undefined);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const ListingProvider = ({ children }: { children: ReactNode }) => {
  const listingApi = useListingsData();
  const clientApi = useClientData();

  const [listings, setListings] = useState<ListingSummary[]>([]);
  const [activeListing, setActiveListing] = useState<ListingDetail | null>(null);

  // Derived loading / error: prefer whichever hook is currently busy
  const loading = listingApi.loading || clientApi.loading;
  const error = listingApi.error || clientApi.error;

  // ── Lists ──────────────────────────────────────────────────────────────────

  const loadUserListings = useCallback(async (userId: string) => {
    const data = await listingApi.fetchUserListings(userId);
    setListings(data);
  }, [listingApi]);

  const loadAgencyListings = useCallback(async (agencyId: string) => {
    const data = await listingApi.fetchAgencyListings(agencyId);
    setListings(data);
  }, [listingApi]);

  // ── Active listing ─────────────────────────────────────────────────────────

  const openListing = useCallback(async (id: string) => {
    const detail = await listingApi.fetchListing(id);
    setActiveListing(detail);
  }, [listingApi]);

  const clearActiveListing = useCallback(() => {
    setActiveListing(null);
  }, []);

  // ── Listing CRUD ───────────────────────────────────────────────────────────

  const createListing = useCallback(async (payload: CreateListingPayload): Promise<ListingSummary> => {
    const created = await listingApi.createListing(payload);
    setListings(prev => [created, ...prev]);
    return created;
  }, [listingApi]);

  const updateListing = useCallback(async (id: string, payload: UpdateListingPayload) => {
    const updated = await listingApi.updateListing(id, payload);
    setListings(prev => prev.map(l => l.id === id ? { ...l, ...updated } : l));
    if (activeListing?.id === id) {
      setActiveListing(updated);
    }
  }, [listingApi, activeListing]);

  const deleteListing = useCallback(async (id: string) => {
    await listingApi.deleteListing(id);
    setListings(prev => prev.filter(l => l.id !== id));
    if (activeListing?.id === id) {
      setActiveListing(null);
    }
  }, [listingApi, activeListing]);

  // ─────────────────────────────────────────────────────────────────────────

  return (
    <ListingContext.Provider value={{
      listings,
      activeListing,
      loading,
      error,
      loadUserListings,
      loadAgencyListings,
      fetchUserListings: listingApi.fetchUserListings,
      fetchAgencyListings: listingApi.fetchAgencyListings,
      openListing,
      clearActiveListing,
      createListing,
      updateListing,
      deleteListing,
      fetchListingTypes: listingApi.fetchListingTypes,
      fetchDocumentTypes: listingApi.fetchDocumentTypes,
      fetchClientDocumentTypes: listingApi.fetchClientDocumentTypes,
      fetchClientTypes: clientApi.fetchClientTypes,
      fetchClientsByListing: clientApi.fetchClientsByListing,
      createClient: clientApi.createClient,
      updateClient: clientApi.updateClient,
      deleteClient: clientApi.deleteClient,
      uploadClientDocument: listingApi.uploadClientDocument,
      uploadListingDocument: listingApi.uploadListingDocument,
      uploadListingImage: listingApi.uploadListingImage,
      deleteListingImage: listingApi.deleteListingImage,
      deleteListingDocument: listingApi.deleteListingDocument,
      deleteClientDocument: listingApi.deleteClientDocument,
    }}>
      {children}
    </ListingContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useListingContext = () => {
  const context = useContext(ListingContext);
  if (!context) {
    throw new Error('useListingContext must be used within a ListingProvider');
  }
  return context;
};

